import React from 'react';
import { useTheme } from '../ThemeProvider/ThemeProvider';

/**
 * Props interface for UniversalIcon component
 * Defines the configuration options for displaying icons in different formats
 */
interface UniversalIconProps {
  type: 'svg' | 'image'; // Icon type - determines rendering method
  src?: string; // Source URL for image icons (required when type is 'image')
  alt?: string; // Alternative text for accessibility (especially important for images)
  size?: number; // Icon size in pixels (applied to both width and height)
  className?: string; // Additional CSS classes for custom styling
  children?: React.ReactNode; // SVG content when type is 'svg'
  colorType?: 'accent' | 'neutral' | 'white' | 'disabled'; // Predefined color theme variants
  onClick?: React.MouseEventHandler<HTMLDivElement | HTMLImageElement>; // Click handler for interactive icons
  style?: React.CSSProperties; // Inline styles for additional customization
}

/**
 * UniversalIcon - A flexible icon component supporting multiple icon formats
 * 
 * This component provides a unified interface for displaying icons in different formats,
 * with support for both SVG and image-based icons. It includes theming support,
 * accessibility features, and interactive capabilities.
 * 
 * Features:
 * - Support for SVG and image icon types
 * - Theme-aware color variants (accent, neutral, white)
 * - Customizable sizing with consistent aspect ratio
 * - Click handling for interactive icons
 * - Accessibility support with alt text
 * - Flexible styling through className and style props
 * - Integration with theme provider system
 * 
 * @param props - Component configuration and content
 * @returns Rendered icon element or null if unsupported type
 */
export const UniversalIcon: React.FC<UniversalIconProps> = ({
  type, // Icon rendering type ('svg' or 'image')
  src, // Image source URL (used with type='image')
  alt, // Alternative text for accessibility
  size, // Icon dimensions in pixels
  className = '', // Additional CSS classes
  children, // SVG content (used with type='svg')
  colorType = '', // Color theme variant
  onClick, // Click event handler
  style // Inline style overrides
}) => {
  /**
   * Render SVG-based icons
   * Creates a wrapper div containing SVG content with theme-aware styling
   */
  if (type === 'svg') {
    return (
      <div
        // Apply CSS classes for styling and theming using BEM methodology
        className={`universal-icon universal-icon--svg universal-icon--${colorType} ${className}`}
        style={{ 
          width: size,  // Set icon width
          height: size, // Set icon height (maintains square aspect ratio)
          ...style      // Merge additional inline styles
        }}
        onClick={onClick} // Attach click handler for interactive behavior
      >
        {children} {/* Render SVG content */}
      </div>
    );
  }

  /**
   * Return null for unsupported icon types
   * Prevents rendering errors and provides graceful fallback
   * Note: Image type handling is not implemented in current version
   */
  return null;
};